import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Box,
  Tabs,
  Tab,
  Badge,
  Paper,
  Container,
} from "@mui/material";
import {
  Search as AnalysisIcon,
  SportsEsports as GameIcon,
  AddCircleOutline as NewGameIcon,
} from "@mui/icons-material";

import { useAuth } from "./chess/lichess/LichessAuth";
import { CurrentGame } from "./chess/lichess/LichessClient";
import ChessBoardAnalysis from "./chess/analysis/ChessBoardAnalysis";
import LichessGame from "./chess/ChessBoard";
import CurrentGamesGrid from "./chess/games/CurrentGamesGrid";
import NewGameTab from "./chess/games/NewGameTab";

export default function Dashboard() {
  const { me } = useAuth();
  const [activeTab, setActiveTab] = useState<number>(0);
  const [currentGames, setCurrentGames] = useState<CurrentGame[]>([]);
  const [selectedGame, setSelectedGame] = useState<CurrentGame | null>(null);
  const [isLoadingGames, setIsLoadingGames] = useState<boolean>(false);

  const loadGames = useCallback(async () => {
    if (!me) return;
    setIsLoadingGames(true);
    try {
      const games = await me.lichessClient.getCurrentGames();
      setCurrentGames(games || []);
      // If a game is selected, update it with fresh data if still playing, but never auto-close it when finished
      setSelectedGame((prev) => {
        if (!prev) return null;
        const fresh = games?.find((g) => g.gameId === prev.gameId);
        return fresh || prev;
      });
    } catch (err) {
      console.error("Failed to load active games:", err);
    } finally {
      setIsLoadingGames(false);
    }
  }, [me]);

  const loadGamesRef = useRef(loadGames);
  loadGamesRef.current = loadGames;

  useEffect(() => {
    loadGames();
  }, [loadGames]);

  // Auto-refresh active games periodically
  useEffect(() => {
    if (!me) return;
    const interval = setInterval(() => {
      loadGamesRef.current();
    }, 15000);
    return () => clearInterval(interval);
  }, [me]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    // Reset selected game when switching tabs
    setSelectedGame(null);
    if (newValue === 1) {
      loadGames();
    }
  };

  const handleSelectGame = (gameId: string) => {
    const game = currentGames.find((g) => g.gameId === gameId);
    if (game) {
      setSelectedGame(game);
    }
  };

  return (
    <Box sx={{ bgcolor: "background.default", minHeight: "100vh", pb: 6 }}>
      <Container maxWidth="lg" sx={{ px: { xs: 1, sm: 3 } }}>
        {/* Navigation Tabs Header */}
        <Paper
          elevation={2}
          sx={{
            mb: 4,
            borderRadius: 3,
            backgroundColor: "#22272e",
            border: "1px solid rgba(255, 255, 255, 0.1)",
            overflow: "hidden",
          }}
        >
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="fullWidth"
            textColor="primary"
            indicatorColor="primary"
            sx={{
              "& .MuiTab-root": {
                py: 2,
                color: "text.secondary",
                fontWeight: 600,
                fontSize: { xs: "0.85rem", sm: "0.95rem" },
                textTransform: "none",
                transition: "all 0.2s ease",
                "&.Mui-selected": {
                  color: "primary.light",
                  backgroundColor: "rgba(59, 130, 246, 0.08)",
                },
              },
            }}
          >
            {/* Tab 1: Analysis */}
            <Tab
              icon={<AnalysisIcon />}
              iconPosition="start"
              label="Analysis"
            />

            {/* Tab 2: Current Games (with circle count badge) */}
            <Tab
              icon={<GameIcon />}
              iconPosition="start"
              label={
                <Box display="flex" alignItems="center" gap={1.5} sx={{ pl: 0.5 }}>
                  <span>Current Games</span>
                  <Badge
                    badgeContent={currentGames.length}
                    color="primary"
                    sx={{
                      ml: 1,
                      "& .MuiBadge-badge": {
                        position: "static",
                        transform: "none",
                        fontWeight: "bold",
                        borderRadius: "50%",
                        minWidth: 22,
                        height: 22,
                        px: 0.75,
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                      },
                    }}
                  />
                </Box>
              }
            />

            {/* Tab 3: New Game */}
            <Tab
              icon={<NewGameIcon />}
              iconPosition="start"
              label="New Game"
            />
          </Tabs>
        </Paper>

        {/* Tab 1 Content: Analysis */}
        {activeTab === 0 && (
          <Box>
            <ChessBoardAnalysis />
          </Box>
        )}

        {/* Tab 2 Content: Current Games */}
        {activeTab === 1 && (
          <Box>
            {selectedGame ? (
              <LichessGame
                key={selectedGame.gameId}
                me={me!}
                currentGame={selectedGame}
                onBackToGrid={() => setSelectedGame(null)}
              />
            ) : (
              <CurrentGamesGrid
                me={me}
                currentGames={currentGames}
                isLoading={isLoadingGames}
                onRefresh={loadGames}
                onSelectGame={handleSelectGame}
                onGoToNewGame={() => setActiveTab(2)}
              />
            )}
          </Box>
        )}

        {/* Tab 3 Content: New Game */}
        {activeTab === 2 && (
          <Box>
            <NewGameTab
              me={me}
              onGameCreated={() => {
                loadGames();
                setActiveTab(1);
              }}
            />
          </Box>
        )}
      </Container>
    </Box>
  );
}